import { ArrowForwardIcon } from '@chakra-ui/icons';
import {
  Button,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  Text,
  VStack,
} from '@chakra-ui/react';
import { format, isFriday, nextFriday } from 'date-fns';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  onLogSpecificContact: () => void;
  onLogWeeklyReport: () => void;
}

const PeerSupportLogContactModal = ({ isOpen, onClose, onLogSpecificContact, onLogWeeklyReport }: Props) => {
  const today = new Date();
  const weeklyReportAvailable = isFriday(today);
  const options = [
    {
      title: 'Log Specific Contact',
      description: weeklyReportAvailable ? 'Including the date' : 'Including the date, backdate allowed',
      onClick: onLogSpecificContact,
      disabled: false,
    },
    {
      title: 'Log Weekly Report',
      description: weeklyReportAvailable
        ? 'Multiple contacts, Friday each week'
        : `Next report on Friday ${format(nextFriday(today), 'dd/MM')}`,
      onClick: () => {
        if (isFriday(new Date())) onLogWeeklyReport();
      },
      disabled: !weeklyReportAvailable,
    },
  ];

  return (
    <Modal isOpen={isOpen} onClose={onClose} isCentered>
      <ModalOverlay bg="blackAlpha.700" />
      <ModalContent bg="background.lightBlue" borderRadius="30px" maxW="500px" mx={4} maxH="90vh">
        <ModalHeader pt={{ base: 8, md: 10 }} pb={0} textAlign="center">
          <Text variant="loraSmallTitle" fontStyle="italic" fontWeight={500} fontSize={'24px'}>
            Log Contact
          </Text>
        </ModalHeader>
        <ModalCloseButton color="text.darkBlue" size="lg" top={{ base: 4, md: 10 }} right={{ base: 3, md: 10 }} />
        <ModalBody px={{ base: 5, md: 12 }} pt={{ base: 8, md: 12 }} pb={{ base: 8, md: 14 }} overflowY="auto">
          <VStack spacing={5} maxW="420px" mx="auto">
            {options.map(({ title, description, onClick, disabled }) => (
              <Button
                key={title}
                onClick={onClick}
                isDisabled={disabled}
                variant="unstyled"
                display="flex"
                alignItems="center"
                justifyContent="space-between"
                gap={2}
                w="300px"
                maxW="100%"
                h="76px"
                flexShrink={0}
                p={4}
                bg="extra.white"
                border="1px solid #00417926"
                borderRadius="12px"
                boxShadow="0px 2px 4px 0px #0041790D"
                textAlign="left"
                whiteSpace="normal"
                color="primary.500"
                _hover={{ bg: 'background.lightGray' }}
                _disabled={{ opacity: 0.5, cursor: 'not-allowed' }}
                _focusVisible={{ outline: '2px solid', outlineColor: 'primary.500', outlineOffset: '3px' }}
              >
                <VStack as="span" align="start" spacing={1}>
                  <Text as="span" fontWeight={700} fontSize="16px">
                    {title}
                  </Text>
                  <Text as="span" color="text.mediumGray" fontWeight={600} fontSize="14px" lineHeight="18px">
                    {description}
                  </Text>
                </VStack>
                {!disabled && <ArrowForwardIcon boxSize={4} flexShrink={0} aria-hidden="true" />}
              </Button>
            ))}
          </VStack>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};

export default PeerSupportLogContactModal;
